import React, { useEffect, useRef, useState } from "react";
import MinimumVsTargetChart from "./MinimumVsTargetChart";
import PreparationProfileChart from "./PreparationProfileChart";
import OfficialStandardsSection from "./OfficialStandardsSection";

const contentByVariant = {
  program: {
    eyebrow: "Voorbereidingsbuffer",
    title: "Minimum halen is niet hetzelfde als voorbereid zijn",
    lead: "De minimale eis is de ondergrens. Niet het einddoel.",
    body: [
      "Meer loopvermogen kan extra marge geven boven de eis van jouw testmoment. Cluster 6, de Fitheidstest Mariniers en de KCT-informatiedagen zijn verschillende beoordelingen. De tabel vermeldt de aanvullende eenheidstest per profiel; het gele bereik is uitsluitend een Mission Movement trainingsdoel.",
      "Mission Movement traint daarom niet alleen richting een voldoende. Je werkt aan een sterkere basis over belastbaarheid, rucking, hardlopen, trekkracht, duwkracht, rompcapaciteit, onderlichaam, zwemmen en mentale tolerantie.",
      "Meer marge geeft geen garantie op selectie. Het kan wel zorgen voor een betere uitgangspositie, omdat je minder afhankelijk bent van een perfecte dag en minder snel op absolute maximale inspanning hoeft te werken.",
    ],
  },
  pricing: {
    eyebrow: "Waarom begeleiding",
    title: "Je betaalt niet voor losse workouts",
    lead: "Je werkt aan een sterkere uitgangspositie.",
    body: [
      "Het verschil zit in structuur, bijsturen en bouwen aan marge. Niet alleen de ondergrens aantikken, maar gericht werken aan een bredere basis die jouw richting beter kan dragen.",
    ],
  },
  check: {
    eyebrow: "Waar sta jij nu",
    title: "Een goede voorbereiding begint met weten waar je staat",
    lead: "De check helpt zichtbaar maken waar je marge mist.",
    body: [
      "Sterk zijn op een enkel onderdeel betekent niet automatisch dat je hele voorbereiding klopt. De check kijkt breder naar startniveau, belastbaarheid, herstel en aandachtspunten.",
    ],
  },
};

const PerformanceBufferSection = ({
  variant = "program",
  compact = false,
  showProfile = true,
  className = "",
}) => {
  const content = contentByVariant[variant] || contentByVariant.program;
  const [hideScrollHelper, setHideScrollHelper] = useState(false);
  const hasScrolledPanelRef = useRef(false);
  const scrollHelperTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (scrollHelperTimeoutRef.current) {
        clearTimeout(scrollHelperTimeoutRef.current);
      }
    };
  }, []);

  const handlePanelScroll = (event) => {
    if (hasScrolledPanelRef.current || event.currentTarget.scrollTop <= 0) {
      return;
    }

    hasScrolledPanelRef.current = true;
    scrollHelperTimeoutRef.current = setTimeout(() => {
      setHideScrollHelper(true);
    }, 1000);
  };

  return (
    <>
    <section
      className={`w-full overflow-x-hidden bg-mmPage px-4 py-14 text-mmText sm:px-6 md:py-20 lg:px-8 border-y border-mmBorder ${className}`}
    >
      <div className="mx-auto w-full max-w-7xl">
        <div className="grid min-w-0 gap-8 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] lg:items-start">
          <div className="min-w-0 lg:sticky lg:top-24">
            <p className="font-display uppercase tracking-[0.22em] text-mmAccent text-xs md:text-sm mb-4">
              {content.eyebrow}
            </p>
            <h2 className="mm-h2 text-mmText mb-4">{content.title}</h2>
            <p className="text-white text-lg md:text-xl leading-snug font-semibold mb-5">
              {content.lead}
            </p>
            <div className="space-y-4 text-sm md:text-base leading-relaxed text-mmTextMuted">
              {content.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>

          <div className="min-w-0">
            <div
              className="mm-card mm-scroll-panel relative max-h-none min-w-0 overflow-visible p-4 pb-8 md:max-h-[calc(100vh-120px)] md:overflow-y-auto md:p-6 md:pb-10"
              onScroll={handlePanelScroll}
            >
              <div className="mb-4 hidden justify-end md:flex">
                <p
                  className={`rounded-full border border-mmAccent/40 bg-black/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-mmAccent motion-safe:transition-opacity motion-safe:duration-500 ${
                    hideScrollHelper
                      ? "pointer-events-none opacity-0"
                      : "opacity-100"
                  }`}
                >
                  Scroll voor het volledige overzicht
                </p>
              </div>
              <div className="min-w-0 space-y-7">
                <MinimumVsTargetChart compact={compact} framed={false} />
                {showProfile && (
                  <div className="min-w-0 border-t border-mmBorder pt-7">
                    <PreparationProfileChart compact={compact} framed={false} />
                  </div>
                )}
              </div>
              <div
                className="pointer-events-none sticky bottom-0 -mx-4 mt-4 hidden h-10 bg-gradient-to-t from-mmSurface to-transparent md:block"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
    <OfficialStandardsSection />
    </>
  );
};

export default PerformanceBufferSection;
